import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { locationsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { ArrowRightLeft, Users, Car, DoorOpen, Loader2 } from 'lucide-react';

export function MoveLocationDialog({ open, onClose, location, branches, orgL, onMoved }) {
  const [preview, setPreview] = useState(null);
  const [targetBranchId, setTargetBranchId] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (open && location) {
      setTargetBranchId('');
      setPreview(null);
      setLoadingPreview(true);
      locationsAPI.movePreview(location.id)
        .then((res) => setPreview(res.data))
        .catch((err) => toast.error(err.response?.data?.detail || 'Fehler beim Laden'))
        .finally(() => setLoadingPreview(false));
    }
  }, [open, location]);

  const otherBranches = (branches || []).filter((b) => b.id !== location?.branch_id);

  const handleMove = async () => {
    if (!targetBranchId) {
      toast.error('Bitte eine Ziel-Niederlassung wählen');
      return;
    }
    setMoving(true);
    try {
      const res = await locationsAPI.move(location.id, targetBranchId);
      const d = res.data;
      toast.success(
        `Standort verschoben nach "${d.target_branch_name}" — `
        + `${d.employees_updated} Mitarbeiter, ${d.vehicles_updated} Fahrzeug(e) mitgezogen`
      );
      onMoved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Verschieben');
    } finally {
      setMoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="move-location-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-accent" />
            {orgL?.location || 'Standort'} verschieben
          </DialogTitle>
          <DialogDescription>
            „{location?.name}" in eine andere {orgL?.branch || 'Niederlassung'} verschieben.
            Alle zugehörigen Daten wandern automatisch mit.
          </DialogDescription>
        </DialogHeader>

        {loadingPreview ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lädt…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-secondary/40 p-3 text-sm space-y-2">
              <p className="text-muted-foreground">
                Aktuell in: <span className="font-medium text-foreground">{preview?.current_branch_name || '—'}</span>
              </p>
              <div className="flex flex-wrap gap-4 pt-1">
                <span className="flex items-center gap-1.5" data-testid="move-preview-employees">
                  <Users className="h-4 w-4 text-accent" />
                  {preview?.employee_count ?? 0} Mitarbeiter
                </span>
                <span className="flex items-center gap-1.5" data-testid="move-preview-vehicles">
                  <Car className="h-4 w-4 text-accent" />
                  {preview?.vehicle_count ?? 0} Fahrzeug(e)
                </span>
                <span className="flex items-center gap-1.5" data-testid="move-preview-rooms">
                  <DoorOpen className="h-4 w-4 text-accent" />
                  {preview?.room_count ?? 0} Raum/Räume
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Ziel-{orgL?.branch || 'Niederlassung'}
              </label>
              <Select value={targetBranchId} onValueChange={setTargetBranchId}>
                <SelectTrigger data-testid="move-target-branch-select">
                  <SelectValue placeholder={`${orgL?.branch || 'Niederlassung'} wählen…`} />
                </SelectTrigger>
                <SelectContent>
                  {otherBranches.map((b) => (
                    <SelectItem key={b.id} value={b.id} data-testid={`move-target-branch-${b.id}`}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {otherBranches.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Keine andere {orgL?.branch || 'Niederlassung'} vorhanden.
                </p>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              Mitarbeiter, Fahrzeuge, Räume, Schichten und Maßnahmen dieses Standorts
              bleiben erhalten und werden der neuen {orgL?.branch || 'Niederlassung'} zugeordnet.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={moving} data-testid="move-location-cancel">
            Abbrechen
          </Button>
          <Button
            onClick={handleMove}
            disabled={moving || loadingPreview || !targetBranchId}
            data-testid="move-location-confirm"
          >
            {moving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Verschieben
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
